import { Entity } from '@backstage/catalog-model';
import { FEATURE_DEFINITIONS, FeatureDefinition, getFeatureById } from '../constants/featureDefinitions';

/**
 * Check if an annotation value is valid (present and non-empty)
 * @param value - Annotation value to validate (type-safe with unknown)
 */
function isAnnotationValid(value: unknown): boolean {
  return value !== undefined && value !== null && String(value).trim() !== '';
}

/**
 * Feature Detection Result
 */
export interface FeatureDetectionResult {
  feature: FeatureDefinition;
  isConfigured: boolean;
  missingAnnotations: string[];
  presentAnnotations: string[];
}

/**
 * Check if a specific feature is configured for an entity
 */
export function isFeatureConfigured(entity: Entity, featureId: string): boolean {
  const feature = getFeatureById(featureId);
  if (!feature) return false;

  const annotations = entity.metadata.annotations || {};

  const requiredOk =
    !feature.requiredAnnotations ||
    feature.requiredAnnotations.length === 0 ||
    feature.requiredAnnotations.every(annotation =>
      isAnnotationValid(annotations[annotation]),
    );

  if (feature.anyOfAnnotations && feature.anyOfAnnotations.length > 0) {
    const anyOk = feature.anyOfAnnotations.some(annotation =>
      isAnnotationValid(annotations[annotation]),
    );
    return requiredOk && anyOk;
  }

  // If no required annotations, feature is always available
  if (!feature.requiredAnnotations || feature.requiredAnnotations.length === 0) {
    return true;
  }

  return requiredOk;
}

/**
 * Get detailed detection result for a feature
 */
export function detectFeature(entity: Entity, featureId: string): FeatureDetectionResult | null {
  const feature = getFeatureById(featureId);
  if (!feature) return null;

  const annotations = entity.metadata.annotations || {};
  const allAnnotations = [
    ...(feature.requiredAnnotations || []),
    ...(feature.anyOfAnnotations || []),
    ...(feature.optionalAnnotations || []),
  ];

  const presentAnnotations = allAnnotations.filter(annotation => {
    return isAnnotationValid(annotations[annotation]);
  });

  const missingRequired = (feature.requiredAnnotations || []).filter(
    annotation => !isAnnotationValid(annotations[annotation]),
  );

  const anyOf = feature.anyOfAnnotations || [];
  const anyOfSatisfied =
    anyOf.length === 0 ||
    anyOf.some(annotation => isAnnotationValid(annotations[annotation]));

  const missingAnnotations = [
    ...missingRequired,
    ...(anyOfSatisfied ? [] : anyOf),
  ];

  const isConfigured = missingRequired.length === 0 && anyOfSatisfied;

  return {
    feature,
    isConfigured,
    missingAnnotations,
    presentAnnotations,
  };
}

/**
 * Get all features and their configuration status
 */
export function detectAllFeatures(entity: Entity): FeatureDetectionResult[] {
  return FEATURE_DEFINITIONS.map(feature => detectFeature(entity, feature.id)).filter(
    (result): result is FeatureDetectionResult => result !== null,
  );
}

/**
 * Get configured features
 */
export function getConfiguredFeatures(entity: Entity): FeatureDetectionResult[] {
  return detectAllFeatures(entity).filter(result => result.isConfigured);
}

/**
 * Get available but not configured features
 */
export function getAvailableFeatures(entity: Entity): FeatureDetectionResult[] {
  return detectAllFeatures(entity).filter(result => !result.isConfigured);
}

/**
 * Get feature statistics
 */
export interface FeatureStatistics {
  total: number;
  configured: number;
  available: number;
  percentage: number;
  byCategory: Record<string, { total: number; configured: number }>;
}

export function getFeatureStatistics(allResults: FeatureDetectionResult[]): FeatureStatistics {
  const configured = allResults.filter(r => r.isConfigured).length;
  const total = allResults.length;

  // Group by category
  const byCategory: Record<string, { total: number; configured: number }> = {};
  allResults.forEach(result => {
    const category = result.feature.category;
    if (!byCategory[category]) {
      byCategory[category] = { total: 0, configured: 0 };
    }
    byCategory[category].total++;
    if (result.isConfigured) {
      byCategory[category].configured++;
    }
  });

  return {
    total,
    configured,
    available: total - configured,
    percentage: total > 0 ? Math.round((configured / total) * 100) : 0,
    byCategory,
  };
}

/**
 * Check if entity has GitHub integration
 * Note: Both github-actions and pull-requests require the same annotation,
 * so we can check it directly rather than checking both features.
 */
export function hasGithubIntegration(entity: Entity): boolean {
  const annotations = entity.metadata.annotations || {};
  return isAnnotationValid(annotations['github.com/project-slug']);
}

/**
 * Get next recommended feature to configure
 * Based on what's most commonly used and easiest to set up
 */
export function getNextRecommendedFeature(availableResults: FeatureDetectionResult[]): FeatureDetectionResult | null {
  if (availableResults.length === 0) return null;

  // Priority order for recommendations (intentionally hardcoded for business logic)
  // Ordered by: ease of setup, common usage, and value provided
  // - GitHub features: Most common, easiest to set up (just need project-slug)
  // - TechDocs: High value, moderate setup effort
  // - SonarQube/Kubernetes/Datadog: Requires external service configuration
  const priority = [
    'github-actions-cicd',
    'pull-requests',
    'techdocs',
    'aws-codebuild-cicd',
    'sonarqube',
    'kubernetes',
    'datadog',
  ];

  // Find first available feature in priority order
  for (const featureId of priority) {
    const feature = availableResults.find(f => f.feature.id === featureId);
    if (feature) return feature;
  }

  // If no priority match, return first available
  return availableResults[0];
}

/**
 * Backstage-compatible detection functions for conditional tab rendering
 * These match the pattern used by built-in Backstage functions
 */

export const isTechDocsAvailable = (entity: Entity): boolean => {
  return isFeatureConfigured(entity, 'techdocs');
};

/**
 * Kinds whose entity page does not carry the Docs tab, because they get their
 * own tab set from `entityContentExtensionsModule`.
 */
const KINDS_WITHOUT_DOCS_TAB = ['api', 'user', 'group', 'system', 'domain'];

/**
 * True when the entity page for this entity renders a Docs tab.
 *
 * Lives here rather than in `entityContentExtensionsModule` so that links
 * deep-linking into the tab — the Document view of the catalog page — cannot
 * drift from the rule that decides whether the tab exists at all. A shortcut
 * (`backstage.io/techdocs-entity`) does have this tab: it opens the referenced
 * site. The documents table still links those rows to the TechDocs reader of
 * the referenced entity, because nothing is published under the shortcut's
 * own triplet.
 */
export const hasEntityDocsTab = (entity: Entity): boolean =>
  isTechDocsAvailable(entity) &&
  !KINDS_WITHOUT_DOCS_TAB.includes((entity.kind ?? '').toLowerCase());

export const isDatadogAvailable = (entity: Entity): boolean => {
  return isFeatureConfigured(entity, 'datadog');
};

export const isAdrAvailable = (entity: Entity): boolean => {
  return isFeatureConfigured(entity, 'adrs');
};

export const isGithubContentAvailable = (entity: Entity): boolean => {
  const annotations = entity.metadata.annotations || {};
  return !!annotations['github.com/project-slug'];
};

export const isSonarQubeAvailable = (entity: Entity): boolean => {
  return isFeatureConfigured(entity, 'sonarqube');
};

export const isAwsCodeBuildAvailable = (entity: Entity): boolean => {
  return isFeatureConfigured(entity, 'aws-codebuild-cicd');
};

export const isGithubActionsAvailable = (entity: Entity): boolean => {
  return isFeatureConfigured(entity, 'github-actions-cicd');
};

export const isEcsAvailable = (entity: Entity): boolean => {
  return isFeatureConfigured(entity, 'aws-ecs');
};

export const isKubernetesAvailable = (entity: Entity): boolean => {
  return isFeatureConfigured(entity, 'kubernetes');
};

export const isCicdAvailable = (entity: Entity): boolean => {
  // CI/CD tab is available if either GitHub Actions OR AWS CodeBuild is configured
  return isFeatureConfigured(entity, 'github-actions-cicd') || isFeatureConfigured(entity, 'aws-codebuild-cicd');
};

export const isQetaAvailable = (entity: Entity): boolean => {
  return isFeatureConfigured(entity, 'qeta');
};

export const isTechRadarAvailable = (entity: Entity): boolean => {
  return isFeatureConfigured(entity, 'tech-radar');
};

export const isDevOpsMetricsAvailable = (entity: Entity): boolean => {
  // DevOps Metrics requires a GitHub repository URL from annotations
  const annotations = entity.metadata.annotations || {};
  return !!(
    annotations['github.com/project-slug'] ||
    annotations['backstage.io/source-location']
  );
};

/** Check if URL points to GitHub using proper hostname validation */
const isGitHubHost = (urlString: string): boolean => {
  try {
    const url = new URL(urlString.replace(/^url:/, ''));
    const host = url.hostname.toLowerCase();
    return host === 'github.com' || host.endsWith('.github.com');
  } catch {
    return false;
  }
};

/** Whether the GraphiQL tab should appear (catalog API with `spec.type: graphql`). */
export const isGraphqlTabAvailable = (entity: Entity): boolean => {
  if (entity.kind?.toLowerCase() !== 'api') return false;
  const apiType = (entity.spec as { type?: unknown } | undefined)?.type;
  return typeof apiType === 'string' && apiType.toLowerCase() === 'graphql';
};

export const isYamlEditorAvailable = (entity: Entity): boolean => {
  const annotations = entity.metadata.annotations || {};
  const sourceLocation =
    annotations['backstage.io/source-location'] ||
    annotations['backstage.io/managed-by-location'];
  const githubProjectSlug = annotations['github.com/project-slug'];
  return (
    Boolean(githubProjectSlug) ||
    Boolean(sourceLocation && isGitHubHost(sourceLocation))
  );
};
